import { useState } from "react";
import { MarketSizeInput } from "@/components/MarketSizeInput";
import { MarketSizeReport } from "@/components/MarketSizeReport";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { RegionPicker } from "@/components/RegionPicker";
import { SectorPicker } from "@/components/SectorPicker";
import { MarketAnalysisInputs } from "@/components/MarketAnalysisInputs";
import { AdditionalMarketParameters } from "@/components/AdditionalMarketParameters";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/Navbar";

const Index = () => {
  const [tam, setTam] = useState("");
  const [sam, setSam] = useState("");
  const [som, setSom] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [country, setCountry] = useState("");
  const [businessArea, setBusinessArea] = useState("");
  const [sector, setSector] = useState("");
  
  // Market analysis state
  const [marketSegment, setMarketSegment] = useState("");
  const [targetCustomerType, setTargetCustomerType] = useState("");
  const [potentialCustomers, setPotentialCustomers] = useState("");
  const [revenuePerCustomer, setRevenuePerCustomer] = useState("");
  const [marketAccessibility, setMarketAccessibility] = useState("");
  const [accessibilityLimitations, setAccessibilityLimitations] = useState<string[]>([]);
  const [marketPenetration, setMarketPenetration] = useState("");
  const [customerAcquisitionRate, setCustomerAcquisitionRate] = useState("");
  const [penetrationStrategies, setPenetrationStrategies] = useState<string[]>([]);
  const [marketValidation, setMarketValidation] = useState("");
  const [benchmarkRequested, setBenchmarkRequested] = useState(false);
  const [churnRate, setChurnRate] = useState("");
  const [customerLTV, setCustomerLTV] = useState("");
  const [customerCAC, setCustomerCAC] = useState("");
  const [tamCustomers, setTamCustomers] = useState("");
  const [samCustomers, setSamCustomers] = useState("");
  const [somCustomers, setSomCustomers] = useState("");

  const tamValue = parseFloat(tam) || 0;
  const samValue = parseFloat(sam) || 0;
  const somValue = parseFloat(som) || 0;
  const tamCustomersValue = parseFloat(tamCustomers) || 0;
  const samCustomersValue = parseFloat(samCustomers) || 0;
  const somCustomersValue = parseFloat(somCustomers) || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="py-8">
        <div className="container max-w-6xl">
          <MarketSizeReport 
            tam={tamValue} 
            sam={samValue} 
            som={somValue}
            tamCustomers={tamCustomersValue}
            samCustomers={samCustomersValue}
            somCustomers={somCustomersValue}
            currency={currency}
            country={country}
            businessArea={businessArea}
            sector={sector}
            marketSegment={marketSegment}
            targetCustomerType={targetCustomerType}
            potentialCustomers={parseFloat(potentialCustomers) || 0}
            revenuePerCustomer={parseFloat(revenuePerCustomer) || 0}
            marketAccessibility={parseFloat(marketAccessibility) || 0}
            accessibilityLimitations={accessibilityLimitations}
            marketPenetration={parseFloat(marketPenetration) || 0}
            customerAcquisitionRate={parseFloat(customerAcquisitionRate) || 0}
            penetrationStrategies={penetrationStrategies}
            marketValidation={marketValidation}
            benchmarkRequested={benchmarkRequested}
            churnRate={parseFloat(churnRate) || 0}
            customerLTV={parseFloat(customerLTV) || 0}
            customerCAC={parseFloat(customerCAC) || 0}
          >
            {/* Market Size Inputs */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <MarketSizeInput
                    label="Total Addressable Market (TAM)"
                    value={tam}
                    onChange={setTam}
                    tooltip="The total market demand for a product or service"
                  />
                  <MarketSizeInput
                    label="Serviceable Available Market (SAM)"
                    value={sam}
                    onChange={setSam}
                    tooltip="The portion of TAM targeted by your products and services which is within your geographical reach"
                  />
                  <MarketSizeInput
                    label="Serviceable Obtainable Market (SOM)"
                    value={som}
                    onChange={setSom}
                    tooltip="The portion of SAM that you can capture"
                  />
                </div>
                <div>
                  <MarketSizeInput
                    label="Total Addressable Customers"
                    value={tamCustomers}
                    onChange={setTamCustomers}
                    tooltip="The total number of potential customers for your product or service"
                  />
                  <MarketSizeInput
                    label="Serviceable Available Customers"
                    value={samCustomers}
                    onChange={setSamCustomers}
                    tooltip="The number of customers you can reach with your current resources"
                  />
                  <MarketSizeInput
                    label="Serviceable Obtainable Customers"
                    value={somCustomers}
                    onChange={setSomCustomers}
                    tooltip="The number of customers you can realistically acquire"
                  />
                </div>
              </div>
            </div>

            {/* Currency & Region Inputs */}
            <div className="space-y-4">
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Currency
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <CurrencyPicker value={currency} onChange={setCurrency} />
              </div>
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Country
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <RegionPicker value={country} onChange={setCountry} type="country" />
              </div>
            </div>

            {/* Business Details Inputs */}
            <div className="space-y-4">
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Business Area
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <RegionPicker value={businessArea} onChange={setBusinessArea} type="business_area" />
              </div>
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Sector
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <SectorPicker value={sector} onChange={setSector} />
              </div>
            </div>

            {/* Market Analysis Inputs */}
            <MarketAnalysisInputs
              marketSegment={marketSegment}
              setMarketSegment={setMarketSegment}
              targetCustomerType={targetCustomerType}
              setTargetCustomerType={setTargetCustomerType}
              potentialCustomers={potentialCustomers}
              setPotentialCustomers={setPotentialCustomers}
              revenuePerCustomer={revenuePerCustomer}
              setRevenuePerCustomer={setRevenuePerCustomer}
              marketAccessibility={marketAccessibility}
              setMarketAccessibility={setMarketAccessibility}
              accessibilityLimitations={accessibilityLimitations}
              setAccessibilityLimitations={setAccessibilityLimitations}
              marketPenetration={marketPenetration}
              setMarketPenetration={setMarketPenetration}
              customerAcquisitionRate={customerAcquisitionRate}
              setCustomerAcquisitionRate={setCustomerAcquisitionRate}
              penetrationStrategies={penetrationStrategies}
              setPenetrationStrategies={setPenetrationStrategies}
              marketValidation={marketValidation}
              setMarketValidation={setMarketValidation}
              benchmarkRequested={benchmarkRequested}
              setBenchmarkRequested={setBenchmarkRequested}
              churnRate={churnRate}
              setChurnRate={setChurnRate}
              customerLTV={customerLTV}
              setCustomerLTV={setCustomerLTV}
              customerCAC={customerCAC}
              setCustomerCAC={setCustomerCAC}
            />

            {/* Additional Parameters */}
            <AdditionalMarketParameters
              marketPenetration={marketPenetration}
              setMarketPenetration={setMarketPenetration}
              customerAcquisitionRate={customerAcquisitionRate}
              setCustomerAcquisitionRate={setCustomerAcquisitionRate}
              penetrationStrategies={penetrationStrategies}
              setPenetrationStrategies={setPenetrationStrategies}
              marketValidation={marketValidation}
              setMarketValidation={setMarketValidation}
              benchmarkRequested={benchmarkRequested}
              setBenchmarkRequested={setBenchmarkRequested}
              churnRate={churnRate}
              setChurnRate={setChurnRate}
              customerLTV={customerLTV}
              setCustomerLTV={setCustomerLTV}
              customerCAC={customerCAC}
              setCustomerCAC={setCustomerCAC}
            />
          </MarketSizeReport>
        </div>
      </div>
    </div>
  );
};

export default Index;
